import {
  createListenerMiddleware,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit'
import {
  DisplayProductsMode,
  DisplayValueMode,
  ForecastItem,
  PlanItem,
  PlannedItem,
  Product,
  ReplenishmentPlanState,
  ShipmentItem,
  SortBy,
  Species,
  SubmittedItem,
} from './replenishmentPlan.types.ts'
import { AppDispatch, RootState } from '@/data/store.ts'
import { getFromLocalStorage } from '@/core/utils/getFromLocalStorage.ts'
import { saveToLocalStorage } from '@/core/utils/saveToLocalStorage.ts'

const StorageItems = {
  displayValueMode: 'displayValueMode',
  displayProductsMode: 'displayProductsMode',
  sort: 'sort_v2',
}

function getInitialState(): ReplenishmentPlanState {
  const displayValueMode: DisplayValueMode =
    getFromLocalStorage(StorageItems.displayValueMode) ?? DisplayValueMode.cost

  const displayProductsMode: DisplayProductsMode =
    getFromLocalStorage(StorageItems.displayProductsMode) ??
    DisplayProductsMode.categories

  const sort: { sortBy: SortBy; asc: boolean } = getFromLocalStorage(
    StorageItems.sort,
  ) ?? {
    sortBy: SortBy.alphabetically,
    asc: true,
  }

  return {
    isLoadingPlan: false,
    isSearchingProducts: false,
    products: [],
    planProducts: [],
    plannedItems: {},
    forecastItems: {},
    shipmentItems: {},
    submittedItems: {},
    replaceProductId: null,
    species: [],
    planTableFilter: {
      displayValueMode,
      displayProductsMode,
      sort,
      selectedSpecies: [],
      search: '',
    },
  }
}

export const replenishmentPlanSlice = createSlice({
  name: 'replenishmentPlan',
  initialState: getInitialState(),
  reducers: {
    setIsLoadingPlan: (state, action: PayloadAction<boolean>) => {
      state.isLoadingPlan = action.payload
    },
    setDisplayValueMode: (state, action: PayloadAction<DisplayValueMode>) => {
      state.planTableFilter.displayValueMode = action.payload
    },
    setDisplayProductsMode: (
      state,
      action: PayloadAction<DisplayProductsMode>,
    ) => {
      state.planTableFilter.displayProductsMode = action.payload
    },
    setSortBy: (state, action: PayloadAction<SortBy>) => {
      if (state.planTableFilter.sort.sortBy === action.payload) {
        state.planTableFilter.sort.asc = !state.planTableFilter.sort.asc
      } else {
        state.planTableFilter.sort.sortBy = action.payload
        state.planTableFilter.sort.asc = true
      }
    },
    setSpecies: (state, action: PayloadAction<Species[]>) => {
      state.species = action.payload
    },
    setSelectedSpecies: (state, action: PayloadAction<Species[]>) => {
      state.planTableFilter.selectedSpecies = action.payload
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.planTableFilter.search = action.payload
    },
    setPlanProducts: (state, action: PayloadAction<Product[]>) => {
      state.planProducts = action.payload
    },
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.products = action.payload
    },
    setForecastItems: (
      state,
      action: PayloadAction<PlanItem<ForecastItem>>,
    ) => {
      state.forecastItems = action.payload
    },
    setPlannedItems: (state, action: PayloadAction<PlanItem<PlannedItem>>) => {
      state.plannedItems = action.payload
    },
    setShipments: (state, action: PayloadAction<PlanItem<ShipmentItem[]>>) => {
      state.shipmentItems = action.payload
    },
    setSubmittedItems: (
      state,
      action: PayloadAction<PlanItem<SubmittedItem>>,
    ) => {
      state.submittedItems = action.payload
    },
    setProductPlannedItem(
      state,
      action: PayloadAction<{
        productId: number
        month: string
        amount: number
      }>,
    ) {
      const { productId, month, amount } = action.payload

      if (!state.plannedItems[productId]) {
        state.plannedItems[productId] = {}
      }

      state.plannedItems[productId]![month] = { amount }
    },
    addProductToPlan: (
      state,
      action: PayloadAction<{
        product: Product
        salableProductId: number
        plannedItems: PlanItem<PlannedItem>
        forecastItems: PlanItem<ForecastItem>
        shipmentItems: PlanItem<ShipmentItem[]>
      }>,
    ) => {
      const {
        product,
        salableProductId,
        plannedItems,
        forecastItems,
        shipmentItems,
      } = action.payload

      if (
        !state.planProducts.some((planProduct) => planProduct.id === product.id)
      ) {
        state.planProducts.push({
          ...product,
          isInPlan: true,
          salableProductId,
        })
      }

      state.plannedItems = { ...state.plannedItems, ...plannedItems }
      state.forecastItems = { ...state.forecastItems, ...forecastItems }
      state.shipmentItems = { ...state.shipmentItems, ...shipmentItems }
    },
    removeProductFromPlan(state, action: PayloadAction<number>) {
      const productId = action.payload

      state.planProducts = state.planProducts.filter(
        (product) => product.id !== productId,
      )

      delete state.plannedItems[productId]
      delete state.forecastItems[productId]
      delete state.shipmentItems[productId]
    },
    handleClearPlanSearch: (state) => {
      state.planTableFilter.search = ''
      state.products = []
    },
    setReplaceProductId: (state, action: PayloadAction<number | null>) => {
      state.replaceProductId = action.payload
    },
    setIsSearchingProducts: (state, action: PayloadAction<boolean>) => {
      state.isSearchingProducts = action.payload
    },
    replaceProductPlan: (
      state,
      action: PayloadAction<{
        oldProductId: number
        newProduct: Product
        newProductPlannedItems: PlanItem<PlannedItem>
        newProductForecastItems: PlanItem<ForecastItem>
        newProductShipmentItems: PlanItem<ShipmentItem[]>
      }>,
    ) => {
      const {
        oldProductId,
        newProduct,
        newProductPlannedItems,
        newProductForecastItems,
        newProductShipmentItems,
      } = action.payload

      // Delete old product from plan
      state.planProducts = state.planProducts.filter(
        (product) => product.id !== oldProductId,
      )

      delete state.plannedItems[oldProductId]
      delete state.forecastItems[oldProductId]
      delete state.shipmentItems[oldProductId]

      // Add new product to plan
      state.planProducts.push(newProduct)

      Object.assign(state.plannedItems, newProductPlannedItems)
      Object.assign(state.forecastItems, newProductForecastItems)
      Object.assign(state.shipmentItems, newProductShipmentItems)
    },
  },
})

const listenerMiddleware = createListenerMiddleware()

listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
  matcher: isAnyOf(
    replenishmentPlanSlice.actions.setDisplayValueMode,
    replenishmentPlanSlice.actions.setDisplayProductsMode,
    replenishmentPlanSlice.actions.setSortBy,
  ),
  effect: (action, listenerApi) => {
    if (replenishmentPlanSlice.actions.setDisplayValueMode.match(action)) {
      saveToLocalStorage(StorageItems.displayValueMode, action.payload)
    } else if (
      replenishmentPlanSlice.actions.setDisplayProductsMode.match(action)
    ) {
      saveToLocalStorage(StorageItems.displayProductsMode, action.payload)
    } else if (replenishmentPlanSlice.actions.setSortBy.match(action)) {
      const state = listenerApi.getState()
      saveToLocalStorage(
        StorageItems.sort,
        state.replenishmentPlan.planTableFilter.sort,
      )
    }
  },
})

const { actions } = replenishmentPlanSlice

export {
  actions as replenishmentPlanActions,
  listenerMiddleware as replenishmentPlanListenerMiddleware,
}
